import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
export function AboutUs() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-280">
      <SEOHead title="About Us | Smith & Jengs Cleaning Services" description="Learn about Smith & Jengs Cleaning Services — our vision, mission, values, and 100% satisfaction guarantee. Professional cleaning in Bicester and Oxfordshire." canonical="https://smithandjengs.com/about-us" data-id="element-281" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'About Us',
      url: 'https://smithandjengs.com/about-us'
    }]} data-id="element-282" />
      <Navbar data-id="element-283" />

      <main className="flex-1" data-id="element-284">
        {/* 1. Hero Banner */}
        <section className="relative w-full h-[60vh] min-h-[500px] max-h-[700px] overflow-hidden" data-id="element-285">
          <div className="absolute inset-0" data-id="element-286">
            <img src="https://cdn.magicpatterns.com/uploads/3JFURjPXF23VB8fYkgvyfU/banner2.jpg" alt="Modern living room with grey sofa" className="w-full h-full object-cover object-center" data-id="element-287" />
            {/* Light overlay to ensure text readability */}
            <div className="absolute inset-0 bg-white/30" data-id="element-288" />
          </div>

          <div className="relative z-10 h-full flex items-center justify-center px-6 md:px-12 pt-20" data-id="element-289">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="text-center" data-id="element-290">
              <h1 className="font-bebas text-[#1a2744] leading-none tracking-wide flex items-baseline justify-center gap-4" data-id="element-291">
                <span className="text-7xl md:text-[9rem] lg:text-[11rem]" data-id="element-292">
                  ABOUT
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-7xl md:text-[8rem] lg:text-[10rem]" data-id="element-293">
                  Us
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        {/* 2. Our Goal Section */}
        <section className="w-full bg-white py-20 md:py-32 px-6 md:px-12 overflow-hidden" data-id="element-294">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-295">
            {/* Left: Text */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="flex flex-col z-10 lg:-mr-20" data-id="element-296">
              <h2 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-8" data-id="element-297">
                <span className="text-6xl md:text-[6.5rem] lg:text-[7.5rem] block" data-id="element-298">
                  OUR GOAL IS TO
                </span>
                <span className="text-6xl md:text-[6.5rem] lg:text-[7.5rem] block" data-id="element-299">
                  MAKE YOUR LIFE
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-7xl md:text-[7.5rem] lg:text-[8.5rem] block mt-2" data-id="element-300">
                  easier
                </span>
              </h2>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-sm font-medium" data-id="element-301">
                We make life easier for our clients by providing easy, reliable
                solutions for all their residential and commercial cleaning
                needs backed by world-class customer service.
              </p>
            </motion.div>

            {/* Right: Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-[600px]" data-id="element-302">
              <img src="https://cdn.magicpatterns.com/uploads/2WeZZixMbHWHWumyRho3jN/sj-website-about-us-img1.png" alt="Modern interior with stairs and sofa" className="w-full h-full object-cover rounded-sm shadow-xl" loading="lazy" data-id="element-303" />
            </motion.div>
          </div>
        </section>

        {/* 3. Vision/Mission/Values Section */}
        <section className="w-full bg-[#f8f9fa] py-20 md:py-28 px-6 md:px-12" data-id="element-304">
          <div className="max-w-7xl mx-auto" data-id="element-305">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16 md:mb-24" data-id="element-306">
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bebas text-[#1a2744] leading-[0.9] tracking-wide max-w-4xl" data-id="element-307">
                WITH MANY YEARS OF EXPERIENCE,
                <br data-id="element-308" />
                REST ASSURED YOU'RE IN GOOD HANDS
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-16" data-id="element-309">
              {/* Vision */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="flex flex-col md:border-r border-gray-300 md:pr-8 lg:pr-16" data-id="element-310">
                <img src="https://cdn.magicpatterns.com/uploads/cNw4ytSvu8TRB5FWzAM39L/sj-website-icon-vision.svg" alt="Vision icon" className="w-16 h-16 mb-6" loading="lazy" data-id="element-311" />
                <h3 className="text-2xl font-bold text-[#1a2744] mb-4" data-id="element-312">
                  Our Vision
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-313">
                  To lead the residential and commercial cleaning industry by
                  providing the highest level of customer service and quality of
                  work. Our goal is to delight each and every one of our
                  customers by meeting their specific cleaning requirements
                  using our stringent operating standards.
                </p>
              </motion.div>

              {/* Mission */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col md:border-r border-gray-300 md:pr-8 lg:pr-16" data-id="element-314">
                <img src="https://cdn.magicpatterns.com/uploads/hfWqi7BxuQgxuC5DJ7Y7Uq/sj-website-icon-mission.svg" alt="Mission icon" className="w-16 h-16 mb-6" loading="lazy" data-id="element-315" />
                <h3 className="text-2xl font-bold text-[#1a2744] mb-4" data-id="element-316">
                  Our Mission
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-317">
                  To provide first-class cleaning services through an
                  unparalleled level of professionalism, supported by a flexible
                  online and offline approach so you can make time for what
                  matters most: YOU!
                </p>
              </motion.div>

              {/* Values */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-col" data-id="element-318">
                <img src="https://cdn.magicpatterns.com/uploads/gvQFgcBNGuC1NT9t4kdNf8/sj-website-icon-values.svg" alt="Values icon" className="w-16 h-16 mb-6" loading="lazy" data-id="element-319" />
                <h3 className="text-2xl font-bold text-[#1a2744] mb-4" data-id="element-320">
                  Our Values
                </h3>
                <ul className="text-gray-600 text-sm leading-relaxed space-y-2" data-id="element-321">
                  <li data-id="element-322">
                    <strong className="text-[#1a2744]" data-id="element-323">People:</strong> Our
                    business is people
                  </li>
                  <li data-id="element-324">
                    <strong className="text-[#1a2744]" data-id="element-325">Pride:</strong> We take
                    pride in what we do
                  </li>
                  <li data-id="element-326">
                    <strong className="text-[#1a2744]" data-id="element-327">Reliability:</strong> We
                    do what we say we'll do
                  </li>
                  <li data-id="element-328">
                    <strong className="text-[#1a2744]" data-id="element-329">Team:</strong> We bring
                    out the best in each other
                  </li>
                  <li data-id="element-330">
                    <strong className="text-[#1a2744]" data-id="element-331">Quality:</strong> We
                    constantly improve
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 4. Our Guarantee Section */}
        <section className="w-full bg-white overflow-hidden" data-id="element-332">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-333">
            {/* Left: Image */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative w-full h-[400px] lg:h-[600px]" data-id="element-334">
              <img src="https://cdn.magicpatterns.com/uploads/4dCG9caDvnearhVDSUwvXN/sj-website-about-us-img2.png" alt="Pink cleaning glove with green cloth" className="w-full h-full object-cover" loading="lazy" data-id="element-335" />
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col justify-center px-8 py-16 lg:px-20 lg:py-0" data-id="element-336">
              <h2 className="font-bebas text-[#1a2744] leading-[0.8] tracking-wide mb-8" data-id="element-337">
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-7xl md:text-[8rem] lg:text-[9rem] block mb-2" data-id="element-338">
                  Our
                </span>
                <span className="text-6xl md:text-[7.5rem] lg:text-[8.5rem] block" data-id="element-339">
                  GUARANTEE
                </span>
              </h2>

              <div className="bg-[#3d7a8c] p-8 md:p-10 max-w-lg shadow-lg" data-id="element-340">
                <p className="text-white text-sm md:text-base leading-relaxed font-medium" data-id="element-341">
                  We provide a 100% satisfaction guarantee. If you're unhappy
                  with our work we'll give you a full refund or a re-do. Our
                  business was built on community reputation and that means
                  everything to us.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-342" />
    </div>;
}